import { useMemo } from "react";
import { Flame } from "@phosphor-icons/react";

/**
 * Totals summary card:
 *  - Calories uniform sans-serif font
 *  - Per-macro absolute + relative (g/kg of massa magra) emphasised
 *  - Total noble proteins (proteine nobili) computed upstream from proteici portions
 *  - Optional delta vs TDEE (fabbisogno) when `tdee` is provided
 *
 * Props: totals, massaMagraKg (nullable), proteineNobiliG (nullable), tdee (nullable)
 */
export default function MacrosSummary({ totals, massaMagraKg = null, proteineNobiliG = null, tdee = null }) {
  const P = totals?.protein_g || 0;
  const C = totals?.carbs_g || 0;
  const F = totals?.fat_g || 0;
  const kcal = totals?.calories || 0;
  // Delta plan vs TDEE — positive = surplus, negative = deficit.
  // We consider ±50 kcal a "mantenimento" band to smooth out rounding.
  const delta = tdee && kcal ? kcal - tdee : null;
  const deltaBand = delta == null
    ? null
    : Math.abs(delta) <= 50
      ? { key: "mant", label: "mantenimento", color: "#63705B", bg: "rgba(99,112,91,0.12)" }
      : delta > 0
        ? { key: "surp", label: "surplus", color: "#A65D47", bg: "rgba(166,93,71,0.12)" }
        : { key: "def", label: "deficit", color: "#4A7C59", bg: "rgba(74,124,89,0.12)" };
  const deltaPct = delta != null && tdee ? Math.round((delta / tdee) * 100) : null;
  const totalKcal = Math.max(P * 4 + C * 4 + F * 9, 1);
  const pPct = ((P * 4) / totalKcal) * 100;
  const cPct = ((C * 4) / totalKcal) * 100;
  const fPct = ((F * 9) / totalKcal) * 100;

  const rel = useMemo(() => {
    if (!massaMagraKg || massaMagraKg <= 0) return null;
    const g = (n) => (n / massaMagraKg).toFixed(2).replace(".", ",");
    return {
      P: g(P),
      C: g(C),
      F: g(F),
      kcal: (kcal / massaMagraKg).toFixed(1).replace(".", ","),
      Pnob: proteineNobiliG != null ? g(proteineNobiliG) : null,
    };
  }, [P, C, F, kcal, proteineNobiliG, massaMagraKg]);

  return (
    <div className="border border-[color:var(--border)] rounded-2xl p-6 bg-[color:var(--bg-primary)]" data-testid="macros-summary">
      <div className="label-tiny">Totali giornalieri</div>

      <div className="mt-2 flex items-baseline gap-2 flex-wrap">
        <span className="text-3xl font-medium tabular-nums" data-testid="totals-calories">{kcal}</span>
        <span className="text-sm text-[color:var(--text-secondary)]">kcal</span>
        {rel?.kcal && (
          <span
            className="ml-auto text-xs tabular-nums px-2.5 py-1 rounded-full font-medium"
            style={{ background: "rgba(166,93,71,0.12)", color: "var(--accent)" }}
            data-testid="kcal-per-kg"
          >
            {rel.kcal} kcal/kg MM
          </span>
        )}
      </div>

      <div className="mt-4 h-2.5 w-full rounded-full overflow-hidden flex" style={{ background: "var(--bg-tertiary)" }}>
        <div style={{ width: `${pPct}%`, background: "#A65D47" }} />
        <div style={{ width: `${cPct}%`, background: "#B08C4D" }} />
        <div style={{ width: `${fPct}%`, background: "#63705B" }} />
      </div>

      {deltaBand && (
        <div
          className="mt-4 rounded-xl px-3.5 py-2.5 flex items-center gap-3 flex-wrap"
          style={{ background: deltaBand.color, color: "#FCFAF8" }}
          data-testid="tdee-delta-card"
        >
          <Flame size={16} weight="duotone" style={{ color: "#FCFAF8", flexShrink: 0 }} />
          <div className="flex-1 min-w-[9rem]">
            <div className="label-tiny" style={{ color: "#FCFAF8", opacity: 0.9 }}>
              Δ vs TDEE
            </div>
            <div className="text-[10px] italic mt-0.5" style={{ color: "rgba(252,250,248,0.75)" }}>
              Fabbisogno stimato: <span className="tabular-nums font-medium" style={{ color: "#FCFAF8" }}>{tdee}</span> kcal
            </div>
          </div>
          {/* Single solid band covers value + label + pct so the whole
              delta reads as one atomic wording (per user feedback). */}
          <div
            className="inline-flex items-center gap-2 tabular-nums"
            data-testid="tdee-delta-pill"
          >
            <span className="text-base font-medium leading-none" data-testid="tdee-delta-value">
              {delta > 0 ? "+" : ""}{delta} kcal
            </span>
            <span className="leading-none" style={{ color: "rgba(252,250,248,0.5)" }}>·</span>
            <span
              className="text-[10px] uppercase tracking-widest leading-none font-medium"
              data-testid="tdee-delta-band"
            >
              {deltaBand.label}
            </span>
            <span className="leading-none" style={{ color: "rgba(252,250,248,0.5)" }}>·</span>
            <span className="text-[11px] font-medium leading-none">
              {deltaPct > 0 ? "+" : ""}{deltaPct}%
            </span>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2.5 text-sm">
        <MacroRow color="#A65D47" label="Proteine" value={P} pct={pPct} rel={rel?.P} />
        {proteineNobiliG != null && (
          <div
            className="flex items-center justify-between gap-2 pl-4 -mt-0.5"
            data-testid="proteine-nobili-row"
          >
            <div className="flex items-center gap-2 text-[color:var(--text-secondary)]">
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: "#8B4A2F", opacity: 0.75 }}
              />
              <span className="text-xs italic">di cui nobili</span>
            </div>
            <div className="flex items-center gap-2 tabular-nums text-xs">
              <span data-testid="proteine-nobili-value" className="text-[color:var(--text-primary)] font-medium">
                {proteineNobiliG.toFixed(1).replace(".", ",")}g
              </span>
              {rel?.Pnob && (
                <span
                  className="px-2 py-0.5 rounded-full font-medium"
                  style={{ background: "rgba(139,74,47,0.12)", color: "#8B4A2F" }}
                >
                  {rel.Pnob} g/kg MM
                </span>
              )}
            </div>
          </div>
        )}
        <MacroRow color="#B08C4D" label="Carboidrati" value={C} pct={cPct} rel={rel?.C} />
        <MacroRow color="#63705B" label="Grassi" value={F} pct={fPct} rel={rel?.F} />
      </div>

      {!massaMagraKg && (
        <div className="mt-3 text-[10px] text-[color:var(--text-secondary)] italic">
          Aggiungi un rilievo con massa magra per vedere i valori relativi g/kg MM.
        </div>
      )}
    </div>
  );
}

function MacroRow({ color, label, value, pct, rel }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-2 tabular-nums">
        <span>{value.toFixed(1).replace(".", ",")}g</span>
        <span className="text-[color:var(--text-secondary)] text-xs">{pct.toFixed(0)}%</span>
        {rel && (
          <span
            className="text-xs px-2 py-0.5 rounded-full font-medium"
            style={{ background: `${color}1F`, color }}
          >
            {rel} g/kg MM
          </span>
        )}
      </div>
    </div>
  );
}
